<template>
  <div class="store-funds-page">
    <!-- 页面标题 -->
    <page-header>
      <div>店铺资金账户</div>
    </page-header>

    <!-- 资金概览卡片 -->
    <div class="funds-summary">
      <el-row :gutter="20">
        <el-col :span="6">
          <div class="summary-card total">
            <div class="card-icon">
              <i class="el-icon-money"></i>
            </div>
            <div class="card-content">
              <div class="card-label">累计分润</div>
              <div class="card-value">¥{{ formatCurrency(fundsData.totalProfit) }}</div>
              <div class="card-trend">
                <span class="trend-text">累计获得</span>
              </div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="summary-card balance">
            <div class="card-icon">
              <i class="el-icon-wallet"></i>
            </div>
            <div class="card-content">
              <div class="card-label">账户余额</div>
              <div class="card-value">¥{{ formatCurrency(fundsData.balance) }}</div>
              <div class="card-trend">
                <span class="trend-text">可提现</span>
              </div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="summary-card settled">
            <div class="card-icon">
              <i class="el-icon-check"></i>
            </div>
            <div class="card-content">
              <div class="card-label">已提现</div>
              <div class="card-value">¥{{ formatCurrency(fundsData.settledProfit) }}</div>
              <div class="card-trend">
                <span class="trend-text">累计已提</span>
              </div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="summary-card pending">
            <div class="card-icon">
              <i class="el-icon-time"></i>
            </div>
            <div class="card-content">
              <div class="card-label">待结算</div>
              <div class="card-value">¥{{ formatCurrency(fundsData.pendingProfit) }}</div>
              <div class="card-trend">
                <span class="trend-text">待审核</span>
              </div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 银行卡信息 -->
    <el-card class="bank-card">
      <div slot="header" class="card-header">
        <span><i class="el-icon-bank-card"></i> 银行卡信息</span>
        <el-button type="primary" size="small" @click="openBankCardDialog">
          {{ bankInfo.bankCardNumber ? '修改银行卡' : '绑定银行卡' }}
        </el-button>
      </div>
      <div class="bank-info" v-if="bankInfo.bankCardNumber">
        <el-row :gutter="20">
          <el-col :span="6">
            <div class="bank-item">
              <span class="label">持卡人</span>
              <span class="value">{{ bankInfo.bankUserName || '-' }}</span>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="bank-item">
              <span class="label">开户银行</span>
              <span class="value">{{ bankInfo.bankName || '-' }}</span>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="bank-item">
              <span class="label">银行卡号</span>
              <span class="value">{{ bankInfo.bankCardNumber ? maskBankCard(bankInfo.bankCardNumber) : '-' }}</span>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="bank-item">
              <span class="label">开户支行</span>
              <span class="value">{{ bankInfo.bankBranch || '-' }}</span>
            </div>
          </el-col>
        </el-row>
      </div>
      <div class="bank-empty" v-else>
        <i class="el-icon-info"></i>
        暂未绑定银行卡，请点击右上角绑定
      </div>
    </el-card>

    <!-- 提现记录列表 -->
    <el-card class="record-card">
      <div slot="header" class="card-header">
        <span><i class="el-icon-document"></i> 提现记录</span>
        <el-button type="primary" size="small" @click="openWithdrawDialog">
          <i class="el-icon-plus"></i> 申请提现
        </el-button>
      </div>

      <div v-loading="loading">
        <el-table :data="withdrawList" border stripe>
          <el-table-column label="提现单号" prop="withdrawNo" width="180" align="center" />
          <el-table-column label="提现金额" width="140" align="center">
            <template slot-scope="{ row }">
              <span class="amount">¥{{ formatCurrency(row.amount) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="手续费" width="120" align="center">
            <template slot-scope="{ row }">
              ¥{{ formatCurrency(row.fee || 0) }}
            </template>
          </el-table-column>
          <el-table-column label="实际到账" width="140" align="center">
            <template slot-scope="{ row }">
              <span class="actual-amount">¥{{ formatCurrency(row.actualAmount || row.amount) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="120" align="center">
            <template slot-scope="{ row }">
              <el-tag :type="getStatusType(row.status)" size="small">
                {{ getStatusText(row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="申请时间" prop="addDataTime" width="180" align="center">
            <template slot-scope="{ row }">
              {{ formatDate(row.addDataTime) }}
            </template>
          </el-table-column>
          <el-table-column label="备注" prop="remark" min-width="120" show-overflow-tooltip />
        </el-table>

        <el-empty v-if="!loading && withdrawList.length === 0" description="暂无提现记录" :image-size="120" />

        <!-- 分页 -->
        <div class="pagination-container" v-if="withdrawList.length > 0">
          <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="pagination.page"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pagination.pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="pagination.total"
              background
          />
        </div>
      </div>
    </el-card>

    <!-- ==================== 提现对话框 ==================== -->
    <el-dialog
        title="申请提现"
        :visible.sync="withdrawDialog.visible"
        width="460px"
        @close="handleWithdrawDialogClose"
        append-to-body
        :close-on-click-modal="false"
    >
      <div class="withdraw-info">
        <div class="withdraw-balance">
          <span class="label">可提现余额</span>
          <span class="value">¥{{ formatCurrency(fundsData.balance) }}</span>
        </div>
        <el-form :model="withdrawForm" :rules="withdrawRules" ref="withdrawFormRef" label-width="100px">
          <el-form-item label="提现金额" prop="amount" required>
            <el-input-number
                v-model="withdrawForm.amount"
                :min="1"
                :max="fundsData.balance / 100"
                :precision="2"
                :step="10"
                controls-position="right"
                style="width: 100%;"
                placeholder="请输入提现金额"
            />
            <div class="withdraw-tips">
              <span>最小提现 1.00 元</span>
              <span class="max-tip">最大 {{ formatCurrency(fundsData.balance) }}</span>
            </div>
          </el-form-item>

          <el-divider content-position="center">提现账户信息</el-divider>

          <el-form-item label="持卡人姓名" prop="bankUserName" required>
            <el-input v-model="withdrawForm.bankUserName" placeholder="请输入持卡人姓名" />
          </el-form-item>
          <el-form-item label="开户银行" prop="bankName" required>
            <el-select v-model="withdrawForm.bankName" placeholder="请选择开户银行" style="width: 100%;">
              <el-option label="中国工商银行" value="中国工商银行" />
              <el-option label="中国农业银行" value="中国农业银行" />
              <el-option label="中国银行" value="中国银行" />
              <el-option label="中国建设银行" value="中国建设银行" />
              <el-option label="交通银行" value="交通银行" />
              <el-option label="招商银行" value="招商银行" />
              <el-option label="浦发银行" value="浦发银行" />
              <el-option label="兴业银行" value="兴业银行" />
              <el-option label="中信银行" value="中信银行" />
              <el-option label="民生银行" value="民生银行" />
              <el-option label="光大银行" value="光大银行" />
              <el-option label="平安银行" value="平安银行" />
              <el-option label="其他" value="其他" />
            </el-select>
          </el-form-item>
          <el-form-item label="银行卡号" prop="bankCardNumber" required>
            <el-input v-model="withdrawForm.bankCardNumber" placeholder="请输入银行卡号" maxlength="32" />
          </el-form-item>
          <el-form-item label="开户支行" prop="bankBranch">
            <el-input v-model="withdrawForm.bankBranch" placeholder="请输入开户支行（选填）" />
          </el-form-item>
          <el-form-item label="备注" prop="remark">
            <el-input v-model="withdrawForm.remark" type="textarea" :rows="2" placeholder="选填备注信息" maxlength="100" />
          </el-form-item>
        </el-form>
      </div>
      <span slot="footer">
        <el-button @click="withdrawDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="submitWithdraw" :loading="withdrawDialog.loading">提交申请</el-button>
      </span>
    </el-dialog>

    <!-- ==================== 绑定银行卡对话框 ==================== -->
    <el-dialog
        :title="bankInfo.bankCardNumber ? '修改银行卡' : '绑定银行卡'"
        :visible.sync="bankCardDialog.visible"
        width="420px"
        @close="handleBankCardDialogClose"
        append-to-body
    >
      <el-form :model="bankCardForm" :rules="bankCardRules" ref="bankCardFormRef" label-width="100px">
        <el-form-item label="持卡人姓名" prop="bankUserName" required>
          <el-input v-model="bankCardForm.bankUserName" placeholder="请输入持卡人姓名" />
        </el-form-item>
        <el-form-item label="开户银行" prop="bankName" required>
          <el-select v-model="bankCardForm.bankName" placeholder="请选择开户银行" style="width: 100%;">
            <el-option label="中国工商银行" value="中国工商银行" />
            <el-option label="中国农业银行" value="中国农业银行" />
            <el-option label="中国银行" value="中国银行" />
            <el-option label="中国建设银行" value="中国建设银行" />
            <el-option label="交通银行" value="交通银行" />
            <el-option label="招商银行" value="招商银行" />
            <el-option label="浦发银行" value="浦发银行" />
            <el-option label="兴业银行" value="兴业银行" />
            <el-option label="中信银行" value="中信银行" />
            <el-option label="民生银行" value="民生银行" />
            <el-option label="光大银行" value="光大银行" />
            <el-option label="平安银行" value="平安银行" />
            <el-option label="其他" value="其他" />
          </el-select>
        </el-form-item>
        <el-form-item label="银行卡号" prop="bankCardNumber" required>
          <el-input v-model="bankCardForm.bankCardNumber" placeholder="请输入银行卡号" maxlength="32" />
        </el-form-item>
        <el-form-item label="开户支行" prop="bankBranch">
          <el-input v-model="bankCardForm.bankBranch" placeholder="请输入开户支行（选填）" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="bankCardDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="submitBankCard" :loading="bankCardDialog.loading">保存</el-button>
      </span>
    </el-dialog>

    <!-- ==================== 提现成功对话框 ==================== -->
    <el-dialog
        title="提现申请已提交"
        :visible.sync="withdrawResultDialog.visible"
        width="380px"
        append-to-body
        :close-on-click-modal="false"
    >
      <div class="withdraw-result">
        <div class="result-icon success">
          <i class="el-icon-check"></i>
        </div>
        <div class="result-title">申请已提交</div>
        <div class="result-desc">平台审核中，请耐心等待</div>
        <div class="result-detail">
          <div class="detail-item">
            <span class="label">提现金额</span>
            <span class="value">¥{{ formatCurrency(withdrawResultDialog.amount) }}</span>
          </div>
          <div class="detail-item">
            <span class="label">申请时间</span>
            <span class="value">{{ withdrawResultDialog.time }}</span>
          </div>
          <div class="detail-item">
            <span class="label">状态</span>
            <span class="value status-pending">审核中</span>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button type="primary" @click="withdrawResultDialog.visible = false">知道了</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader";
import api from "@/utils/api";

export default {
  name: 'StoreFunds',
  components: { PageHeader },

  data() {
    return {
      loading: false,

      // 资金数据
      fundsData: {
        merchantId: null,
        merchantName: '',
        totalProfit: 0,
        balance: 0,
        settledProfit: 0,
        pendingProfit: 0
      },

      // 银行卡信息（从资金接口返回）
      bankInfo: {
        bankCardNumber: '',
        bankName: '',
        bankBranch: '',
        bankUserName: '',
        bankCardUpdateTime: 0
      },

      // 提现记录列表
      withdrawList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },

      // 提现对话框
      withdrawDialog: {
        visible: false,
        loading: false
      },

      withdrawForm: {
        amount: null,
        bankUserName: '',
        bankName: '',
        bankCardNumber: '',
        bankBranch: '',
        remark: ''
      },

      withdrawRules: {
        amount: [
          { required: true, message: '请输入提现金额', trigger: 'blur' },
          { type: 'number', min: 1, message: '提现金额不能小于1元', trigger: 'blur' }
        ],
        bankUserName: [
          { required: true, message: '请输入持卡人姓名', trigger: 'blur' }
        ],
        bankName: [
          { required: true, message: '请选择开户银行', trigger: 'change' }
        ],
        bankCardNumber: [
          { required: true, message: '请输入银行卡号', trigger: 'blur' },
          { pattern: /^\d{16,19}$/, message: '请输入正确的银行卡号（16-19位数字）', trigger: 'blur' }
        ]
      },

      // 提现结果对话框
      withdrawResultDialog: {
        visible: false,
        amount: 0,
        time: ''
      },

      // 银行卡对话框
      bankCardDialog: {
        visible: false,
        loading: false
      },

      bankCardForm: {
        bankUserName: '',
        bankName: '',
        bankCardNumber: '',
        bankBranch: ''
      },

      bankCardRules: {
        bankUserName: [
          { required: true, message: '请输入持卡人姓名', trigger: 'blur' }
        ],
        bankName: [
          { required: true, message: '请选择开户银行', trigger: 'change' }
        ],
        bankCardNumber: [
          { required: true, message: '请输入银行卡号', trigger: 'blur' },
          { pattern: /^\d{16,19}$/, message: '请输入正确的银行卡号（16-19位数字）', trigger: 'blur' }
        ]
      }
    };
  },

  mounted() {
    this.loadFundsInfo();
    this.loadWithdrawRecords();
  },

  methods: {
    // ==================== 加载资金信息 ====================
    async loadFundsInfo() {
      try {
        const res = await api.merchant_getFundsInfo();
        if (res.data.code === '000' && res.data.data) {
          this.fundsData = res.data.data;
          // 银行卡信息从资金接口返回
          if (res.data.data.bankCardNumber !== undefined) {
            this.bankInfo = {
              bankCardNumber: res.data.data.bankCardNumber || '',
              bankName: res.data.data.bankName || '',
              bankBranch: res.data.data.bankBranch || '',
              bankUserName: res.data.data.bankUserName || '',
              bankCardUpdateTime: res.data.data.bankCardUpdateTime || 0
            };
          }
        }
      } catch (error) {
        console.error('加载资金信息失败:', error);
        this.$message.error('加载资金信息失败');
      }
    },

    // ==================== 加载提现记录 ====================
    async loadWithdrawRecords() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        };
        const res = await api.merchant_getWithdrawRecords(params);
        if (res.data.code === '000' && res.data.data) {
          const data = res.data.data;
          this.withdrawList = data.list || [];
          this.pagination.total = data.totalCount || 0;
        }
      } catch (error) {
        console.error('加载提现记录失败:', error);
        this.$message.error('加载提现记录失败');
      } finally {
        this.loading = false;
      }
    },

    // ==================== 分页 ====================
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.page = 1;
      this.loadWithdrawRecords();
    },

    handleCurrentChange(page) {
      this.pagination.page = page;
      this.loadWithdrawRecords();
    },

    // ==================== 格式化 ====================
    formatCurrency(value) {
      if (!value && value !== 0) return '0.00';
      return (value / 100).toFixed(2);
    },

    formatDate(timestamp) {
      if (!timestamp || timestamp === 0) return '-';
      try {
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return '-';
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
      } catch (error) {
        return '-';
      }
    },

    maskBankCard(cardNumber) {
      if (!cardNumber) return '';
      if (cardNumber.length <= 8) return cardNumber;
      const start = cardNumber.substring(0, 4);
      const end = cardNumber.substring(cardNumber.length - 4);
      return start + ' **** **** ' + end;
    },

    getStatusType(status) {
      const map = {
        0: 'warning',
        1: 'success',
        2: 'danger'
      };
      return map[status] || '';
    },

    getStatusText(status) {
      const map = {
        0: '审核中',
        1: '已到账',
        2: '已拒绝'
      };
      return map[status] || '未知';
    },

    getCurrentTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },

    // ==================== 提现相关 ====================
    openWithdrawDialog() {
      if (this.fundsData.balance <= 0) {
        this.$message.warning('余额不足，无法提现');
        return;
      }

      this.withdrawForm = {
        amount: null,
        bankUserName: this.bankInfo.bankUserName || '',
        bankName: this.bankInfo.bankName || '',
        bankCardNumber: this.bankInfo.bankCardNumber || '',
        bankBranch: this.bankInfo.bankBranch || '',
        remark: ''
      };

      this.withdrawDialog.visible = true;
      this.$nextTick(() => {
        this.$refs.withdrawFormRef?.clearValidate();
      });
    },

    submitWithdraw() {
      this.$refs.withdrawFormRef.validate((valid) => {
        if (!valid) return;

        const amountCents = Math.round(this.withdrawForm.amount * 100);
        if (amountCents > this.fundsData.balance) {
          this.$message.error('提现金额超过可用余额');
          return;
        }

        const bankChanged =
            this.withdrawForm.bankUserName !== this.bankInfo.bankUserName ||
            this.withdrawForm.bankName !== this.bankInfo.bankName ||
            this.withdrawForm.bankCardNumber !== this.bankInfo.bankCardNumber ||
            this.withdrawForm.bankBranch !== this.bankInfo.bankBranch;

        this.withdrawDialog.loading = true;

        // 模拟提现接口调用
        setTimeout(() => {
          this.withdrawDialog.loading = false;
          this.withdrawDialog.visible = false;

          // 更新余额
          this.fundsData.balance -= amountCents;
          this.fundsData.settledProfit += amountCents;

          // 如果银行卡信息变更，更新
          if (bankChanged) {
            this.bankInfo.bankUserName = this.withdrawForm.bankUserName;
            this.bankInfo.bankName = this.withdrawForm.bankName;
            this.bankInfo.bankCardNumber = this.withdrawForm.bankCardNumber;
            this.bankInfo.bankBranch = this.withdrawForm.bankBranch;
            this.bankInfo.bankCardUpdateTime = Date.now();
          }

          this.withdrawResultDialog.amount = amountCents;
          this.withdrawResultDialog.time = this.getCurrentTime();
          this.withdrawResultDialog.visible = true;

          // 刷新提现记录
          this.pagination.page = 1;
          this.loadWithdrawRecords();

          this.$message.success('提现申请已提交，等待平台审核');
        }, 1500);
      });
    },

    handleWithdrawDialogClose() {
      this.$refs.withdrawFormRef?.resetFields();
      this.withdrawDialog.loading = false;
    },

    // ==================== 银行卡相关 ====================
    openBankCardDialog() {
      this.bankCardForm = {
        bankUserName: this.bankInfo.bankUserName || '',
        bankName: this.bankInfo.bankName || '',
        bankCardNumber: this.bankInfo.bankCardNumber || '',
        bankBranch: this.bankInfo.bankBranch || ''
      };
      this.bankCardDialog.visible = true;
      this.$nextTick(() => {
        this.$refs.bankCardFormRef?.clearValidate();
      });
    },

    submitBankCard() {
      this.$refs.bankCardFormRef.validate((valid) => {
        if (!valid) return;

        this.bankCardDialog.loading = true;

        // 模拟银行卡保存接口
        setTimeout(() => {
          this.bankCardDialog.loading = false;
          this.bankCardDialog.visible = false;

          this.bankInfo.bankUserName = this.bankCardForm.bankUserName;
          this.bankInfo.bankName = this.bankCardForm.bankName;
          this.bankInfo.bankCardNumber = this.bankCardForm.bankCardNumber;
          this.bankInfo.bankBranch = this.bankCardForm.bankBranch;
          this.bankInfo.bankCardUpdateTime = Date.now();

          this.$message.success('银行卡信息保存成功');
        }, 1000);
      });
    },

    handleBankCardDialogClose() {
      this.$refs.bankCardFormRef?.resetFields();
      this.bankCardDialog.loading = false;
    }
  }
};
</script>

<style scoped>
.store-funds-page {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

/* 资金概览卡片 */
.funds-summary {
  margin-bottom: 20px;
}

.summary-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s, box-shadow 0.3s;
  height: 100px;
}

.summary-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.summary-card .card-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  margin-right: 16px;
}

.summary-card.total .card-icon {
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
}
.summary-card.balance .card-icon {
  background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
}
.summary-card.settled .card-icon {
  background: linear-gradient(135deg, #faad14 0%, #d48806 100%);
}
.summary-card.pending .card-icon {
  background: linear-gradient(135deg, #722ed1 0%, #531dab 100%);
}

.summary-card .card-content {
  flex: 1;
}

.summary-card .card-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
}

.summary-card .card-value {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.summary-card .card-trend {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.summary-card .card-trend .trend-text {
  color: #999;
}

/* 银行卡卡片 */
.bank-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header span {
  font-weight: 600;
  font-size: 16px;
}

.card-header span i {
  margin-right: 8px;
  color: #409eff;
}

.bank-info {
  padding: 8px 0;
}

.bank-item {
  display: flex;
  flex-direction: column;
}

.bank-item .label {
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.bank-item .value {
  font-size: 15px;
  font-weight: 500;
  color: #333;
}

.bank-empty {
  text-align: center;
  padding: 20px;
  color: #999;
  font-size: 14px;
}

.bank-empty i {
  margin-right: 8px;
  color: #409eff;
}

/* 提现记录卡片 */
.record-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}

.amount {
  font-weight: 600;
  color: #f56c6c;
}

.actual-amount {
  font-weight: 600;
  color: #67c23a;
}

/* 提现对话框样式 */
.withdraw-info .withdraw-balance {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f6ffed;
  border-radius: 8px;
  margin-bottom: 20px;
}

.withdraw-info .withdraw-balance .label {
  font-size: 14px;
  color: #666;
}

.withdraw-info .withdraw-balance .value {
  font-size: 24px;
  font-weight: 700;
  color: #1890ff;
}

.withdraw-tips {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.withdraw-tips .max-tip {
  color: #1890ff;
}

/* 提现结果对话框样式 */
.withdraw-result {
  text-align: center;
  padding: 16px 0;
}

.withdraw-result .result-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.withdraw-result .result-icon.success {
  background: #f6ffed;
  border: 2px solid #b7eb8f;
}

.withdraw-result .result-icon.success i {
  font-size: 32px;
  color: #52c41a;
}

.withdraw-result .result-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.withdraw-result .result-desc {
  font-size: 14px;
  color: #faad14;
  margin-bottom: 16px;
}

.withdraw-result .result-detail {
  background: #fafafa;
  border-radius: 8px;
  padding: 16px;
  text-align: left;
}

.withdraw-result .result-detail .detail-item {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
}

.withdraw-result .result-detail .detail-item .label {
  color: #666;
}

.withdraw-result .result-detail .detail-item .value {
  font-weight: 500;
  color: #333;
}

.withdraw-result .result-detail .detail-item .value.status-pending {
  color: #faad14;
}

/* 响应式 */
@media (max-width: 768px) {
  .summary-card {
    margin-bottom: 16px;
  }

  .bank-item {
    margin-bottom: 12px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}
</style>